import { useLayoutEffect, useRef, useCallback } from 'react';
import Lenis from 'lenis';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './ScrollStack.css';

/** Document Y of element’s layout box (ignores transforms on the element). */
function getLayoutOffsetTop(element) {
	let y = 0;
	let node = element;
	while (node) {
		y += node.offsetTop;
		node = node.offsetParent;
	}
	return y;
}

/**
 * Cumulative stack offset for card index (used for pinStart / translateY / blur thresholds).
 * - number: same gap between each level → `distance * index`
 * - number[]: gap before card k is `arr[k - 1]`; sum of `arr[0]…arr[index - 1]]`; out-of-range uses last value
 */
function stackOffsetBeforeIndex(itemStackDistance, index) {
	if (index <= 0) {
		return 0;
	}
	if (typeof itemStackDistance === 'number') {
		return itemStackDistance * index;
	}
	if (Array.isArray(itemStackDistance)) {
		const arr = itemStackDistance;
		const fallback = arr.length ? arr[arr.length - 1] : 0;
		let sum = 0;
		for (let k = 0; k < index; k++) {
			sum += arr[k] ?? fallback;
		}
		return sum;
	}
	return 0;
}

export const ScrollStackItem = ({ children, itemClassName = '' }) => (
	<div className={`scroll-stack-card ${itemClassName}`.trim()}>{children}</div>
);

const ScrollStack = ({
	children,
	className = '',
	itemDistance = 100,
	itemScale = 0.1,
	/** Same gap every level (number), or per-step gaps in px (array; cumulative for pinStart). */
	itemStackDistance = 80,
	stackPosition = '5%',
	scaleEndPosition = '10%',
	baseScale = 0.7,
	scaleDuration = 1.5,
	rotationAmount = 0,
	blurAmount = 0,
	useWindowScroll = false,
	onStackComplete,
}) => {
	const scrollerRef = useRef(null);
	const stackCompletedRef = useRef(false);
	const animationFrameRef = useRef(null);
	const lenisRef = useRef(null);
	const cardsRef = useRef([]);
	const lastTransformsRef = useRef(new Map());
	const isUpdatingRef = useRef(false);

	const calculateProgress = useCallback((scrollTop, start, end) => {
		if (scrollTop < start) return 0;
		if (scrollTop > end) return 1;
		return (scrollTop - start) / (end - start);
	}, []);

	const parsePercentage = useCallback((value, containerHeight) => {
		if (typeof value === 'string' && value.includes('%')) {
			return (parseFloat(value) / 100) * containerHeight;
		}
		return parseFloat(value);
	}, []);

	const getScrollData = useCallback(() => {
		if (useWindowScroll) {
			return {
				scrollTop: window.scrollY,
				containerHeight: window.innerHeight,
				scrollContainer: document.documentElement,
			};
		}
		const scroller = scrollerRef.current;
		return {
			scrollTop: scroller.scrollTop,
			containerHeight: scroller.clientHeight,
			scrollContainer: scroller,
		};
	}, [useWindowScroll]);

	const getElementOffset = useCallback(
		(element) => {
			if (useWindowScroll) {
				return getLayoutOffsetTop(element);
			}
			return element.offsetTop;
		},
		[useWindowScroll]
	);

	const updateCardTransforms = useCallback(() => {
		if (!cardsRef.current.length || isUpdatingRef.current) return;

		isUpdatingRef.current = true;

		const { scrollTop, containerHeight } = getScrollData();
		const stackPositionPx = parsePercentage(stackPosition, containerHeight);
		const scaleEndPositionPx = parsePercentage(scaleEndPosition, containerHeight);

		const endElement = scrollerRef.current?.querySelector('.scroll-stack-end');

		const endElementTop = endElement ? getElementOffset(endElement) : 0;

		/* Scale runs between two scroll positions derived from stackPosition vs scaleEndPosition.
		 * If those resolve to almost the same value (e.g. 80px vs 10% viewport), progress jumps in a few px — enforce a minimum span. */
		const MIN_SCALE_SCROLL_SPAN = 280;

		cardsRef.current.forEach((card, i) => {
			if (!card) return;

			const cardTop = getElementOffset(card);
			const stackShift = stackOffsetBeforeIndex(itemStackDistance, i);
			const pinStart = cardTop - stackPositionPx - stackShift;
			const rawScaleA = pinStart;
			const rawScaleB = cardTop - scaleEndPositionPx;
			let scaleScrollStart = Math.min(rawScaleA, rawScaleB);
			let scaleScrollEnd = Math.max(rawScaleA, rawScaleB);
			if (scaleScrollEnd - scaleScrollStart < MIN_SCALE_SCROLL_SPAN) {
				const mid = (scaleScrollStart + scaleScrollEnd) / 2;
				scaleScrollStart = mid - MIN_SCALE_SCROLL_SPAN / 2;
				scaleScrollEnd = mid + MIN_SCALE_SCROLL_SPAN / 2;
			}

			const pinEnd = endElementTop - containerHeight / 2;

			const scaleProgress = calculateProgress(scrollTop, scaleScrollStart, scaleScrollEnd);
			const targetScale = baseScale + i * itemScale;
			const scale = 1 - scaleProgress * (1 - targetScale);
			const rotation = rotationAmount ? i * rotationAmount * scaleProgress : 0;

			let blur = 0;
			if (blurAmount) {
				let topCardIndex = 0;
				for (let j = 0; j < cardsRef.current.length; j++) {
					const jCardTop = getElementOffset(cardsRef.current[j]);
					const jTriggerStart = jCardTop - stackPositionPx - stackOffsetBeforeIndex(itemStackDistance, j);
					if (scrollTop >= jTriggerStart) {
						topCardIndex = j;
					}
				}

				if (i < topCardIndex) {
					const depthInStack = topCardIndex - i;
					blur = Math.max(0, depthInStack * blurAmount);
				}
			}

			let translateY = 0;
			const isPinned = scrollTop >= pinStart && scrollTop <= pinEnd;

			if (isPinned) {
				translateY = scrollTop - cardTop + stackPositionPx + stackShift;
			} else if (scrollTop > pinEnd) {
				translateY = pinEnd - cardTop + stackPositionPx + stackShift;
			}

			const newTransform = {
				translateY: Math.round(translateY),
				scale: Math.round(scale * 1000) / 1000,
				rotation: Math.round(rotation * 10) / 10,
				blur: Math.round(blur * 10) / 10,
			};

			const lastTransform = lastTransformsRef.current.get(i);
			const hasChanged =
				!lastTransform ||
				Math.abs(lastTransform.translateY - newTransform.translateY) > 0.5 ||
				Math.abs(lastTransform.scale - newTransform.scale) > 0.002 ||
				Math.abs(lastTransform.rotation - newTransform.rotation) > 0.15 ||
				Math.abs(lastTransform.blur - newTransform.blur) > 0.15;

			if (hasChanged) {
				const transform = `translate3d(0, ${newTransform.translateY}px, 0) scale(${newTransform.scale}) rotate(${newTransform.rotation}deg)`;
				const filter = newTransform.blur > 0 ? `blur(${newTransform.blur}px)` : '';

				card.style.transform = transform;
				card.style.filter = filter;

				lastTransformsRef.current.set(i, newTransform);
			}

			if (i === cardsRef.current.length - 1) {
				const isInView = scrollTop >= pinStart && scrollTop <= pinEnd;
				if (isInView && !stackCompletedRef.current) {
					stackCompletedRef.current = true;
					onStackComplete?.();
				} else if (!isInView && stackCompletedRef.current) {
					stackCompletedRef.current = false;
				}
			}
		});

		isUpdatingRef.current = false;
	}, [
		itemScale,
		itemStackDistance,
		stackPosition,
		scaleEndPosition,
		baseScale,
		rotationAmount,
		blurAmount,
		useWindowScroll,
		onStackComplete,
		calculateProgress,
		parsePercentage,
		getScrollData,
		getElementOffset,
	]);

	const handleScroll = useCallback(() => {
		updateCardTransforms();
	}, [updateCardTransforms]);

	const setupLenis = useCallback(() => {
		if (useWindowScroll) {
			const lenis = new Lenis({
				duration: 1.2,
				easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
				smoothWheel: true,
				touchMultiplier: 2,
				infinite: false,
				wheelMultiplier: 1,
				lerp: 0.1,
				syncTouch: true,
				syncTouchLerp: 0.075,
			});

			lenis.on('scroll', ScrollTrigger.update);
			lenis.on('scroll', handleScroll);

			const raf = (time) => {
				lenis.raf(time);
				animationFrameRef.current = requestAnimationFrame(raf);
			};
			animationFrameRef.current = requestAnimationFrame(raf);

			lenisRef.current = lenis;
			return lenis;
		}
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const lenis = new Lenis({
			wrapper: scroller,
			content: scroller.querySelector('.scroll-stack-inner'),
			duration: 1.2,
			easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
			smoothWheel: true,
			touchMultiplier: 2,
			infinite: false,
			gestureOrientationHandler: true,
			normalizeWheel: true,
			wheelMultiplier: 1,
			touchInertiaMultiplier: 35,
			lerp: 0.1,
			syncTouch: true,
			syncTouchLerp: 0.075,
			touchInertia: 0.6,
		});

		lenis.on('scroll', ScrollTrigger.update);
		lenis.on('scroll', handleScroll);

		const raf = (time) => {
			lenis.raf(time);
			animationFrameRef.current = requestAnimationFrame(raf);
		};
		animationFrameRef.current = requestAnimationFrame(raf);

		lenisRef.current = lenis;
		return lenis;
	}, [handleScroll, useWindowScroll]);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const cards = Array.from(scroller.querySelectorAll('.scroll-stack-card'));
		const transformsCache = lastTransformsRef.current;
		const isMobile = typeof window !== 'undefined' && window.innerWidth <= 782;

		if (isMobile) {
			return () => {
				stackCompletedRef.current = false;
				cardsRef.current = [];
				transformsCache.clear();
			};
		}

		cardsRef.current = cards;

		cards.forEach((card, i) => {
			if (i < cards.length - 1) {
				card.style.marginBottom = `${itemDistance}px`;
			}
			card.style.willChange = 'transform, filter';
			card.style.transformOrigin = 'top center';
			card.style.backfaceVisibility = 'hidden';
			card.style.transform = 'translateZ(0)';
			card.style.webkitTransform = 'translateZ(0)';
			card.style.perspective = '1000px';
			card.style.webkitPerspective = '1000px';
		});

		setupLenis();

		updateCardTransforms();

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
			if (lenisRef.current) {
				lenisRef.current.destroy();
			}
			stackCompletedRef.current = false;
			cardsRef.current = [];
			transformsCache.clear();
			isUpdatingRef.current = false;
		};
	}, [
		itemDistance,
		itemScale,
		itemStackDistance,
		stackPosition,
		scaleEndPosition,
		baseScale,
		scaleDuration,
		rotationAmount,
		blurAmount,
		useWindowScroll,
		onStackComplete,
		setupLenis,
		updateCardTransforms,
	]);

	const scrollerClass = [
		'scroll-stack-scroller',
		useWindowScroll ? 'scroll-stack-scroller--window' : '',
		className,
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={scrollerClass.trim()} ref={scrollerRef}>
			<div className="scroll-stack-inner">
				{children}
				<div className="scroll-stack-end" aria-hidden="true" />
			</div>
		</div>
	);
};

export default ScrollStack;

